/**
 * Control schemes (pure, no DOM): held move keys + mouse buttons + autorun → the move direction
 * and the facing the local player asks the sim for. Everything here is unit-tested.
 *
 *  - 'action' (default): the hero keeps its facing. W/S forward / backpedal along it, A/D strafe
 *    (full speed, no turn), diagonals combine. Right mouse held (mouselook): the facing follows the
 *    camera yaw continuously and movement is relative to it; both buttons run forward. Left drag
 *    only orbits the camera. Optional turn-left / turn-right keys (Controls settings, unbound by
 *    default) turn like WoW's. Standing still nothing turns the hero except a cast / auto-attack
 *    start on a target (PlayerController faces it then; no continuous auto-face).
 *  - 'camera' (legacy): WASD move relative to the camera; the hero faces where it runs
 *    (PlayerController sends face = move direction). Left / right drag orbit the camera.
 *  - 'wow' (classic WoW controls): facing is separate from movement.
 *      · Right mouse held (mouselook): the hero faces the camera yaw; W/S forward / back along it,
 *        A/D strafe; both buttons run forward.
 *      · Otherwise: A/D turn the hero (π rad/s, ×0.75 while moving), W/S forward / backpedal
 *        along the facing. Left drag orbits the camera only.
 *    The sim slows a human moving against its facing to 4.5/7 (backpedal, spatial.moveUnit).
 *  - Autorun (both schemes): NumLock / Mouse4 toggle it; W or S down, or pressing both mouse
 *    buttons, cancels it.
 *
 * The output is ordinary `move` / `face` commands (PlayerController.drain), so prediction
 * (localMotion, net/predict.ts) and the party room need nothing new.
 */
import type { Vec2 } from '@keystone/sim';
import type { MoveKey } from './bindings';

export type ControlScheme = 'action' | 'camera' | 'wow';
export const CONTROL_SCHEMES: readonly ControlScheme[] = ['action', 'wow', 'camera'];

/** The client steers the facing itself (separately from the move direction): 'action' and 'wow'. */
export const steersFacing = (scheme: ControlScheme): boolean => scheme !== 'camera';

/** Keyboard turn rate (rad/s), WoW's π. */
export const TURN_RATE = Math.PI;
/** Turn rate multiplier while moving. */
export const TURN_MOVING_FACTOR = 0.75;
/** A client-steered facing the sim has not confirmed yet is kept this long, then the sim's wins. */
export const FACING_HOLD_MS = 400;
/** Facing differences below this count as "the same" (matches PlayerController's resend threshold). */
export const FACING_EPS = 0.02;

export interface MouseButtons {
  left: boolean;
  right: boolean;
}

export interface ControlInput {
  scheme: ControlScheme;
  held: ReadonlySet<MoveKey>;
  buttons: MouseButtons;
  autorun: boolean;
  /** Ground direction the camera looks (0 = +x, CCW positive). */
  cameraYaw: number;
  /** The hero's current facing (client-steered or the sim's). */
  facing: number;
}

/** Right mouse held in action / WoW controls: the camera steers the hero. */
export const isMouselook = (scheme: ControlScheme, b: MouseButtons): boolean => steersFacing(scheme) && b.right;

/** Forward axis (-1..1): keys, autorun, and (mouselook) both buttons. */
export function forwardAxis(i: ControlInput): number {
  const keys = (i.held.has('forward') ? 1 : 0) - (i.held.has('back') ? 1 : 0);
  const run = i.autorun || (isMouselook(i.scheme, i.buttons) && i.buttons.left) ? 1 : 0;
  return Math.max(-1, Math.min(1, keys + run));
}

/**
 * Keyboard turn axis: +1 = turn left (CCW), -1 = right. WoW controls: A/D (and the optional turn
 * keys); action: only the optional turn keys. Never while right mouse steers, nor in 'camera'.
 */
export function turnAxis(scheme: ControlScheme, held: ReadonlySet<MoveKey>, b: MouseButtons): number {
  if (!steersFacing(scheme) || b.right) return 0;
  const l = held.has('turnLeft') || (scheme === 'wow' && held.has('left'));
  const r = held.has('turnRight') || (scheme === 'wow' && held.has('right'));
  return (l ? 1 : 0) - (r ? 1 : 0);
}

/** Move direction in sim coordinates (unit vector or zero). */
export function controlMove(i: ControlInput): Vec2 {
  const f = forwardAxis(i);
  let r = 0;
  let basis = i.cameraYaw;
  // A/D strafe: always in 'action' (and 'camera'); WoW controls only while mouselooking (else they turn).
  if (i.scheme !== 'wow' || isMouselook(i.scheme, i.buttons)) r = (i.held.has('right') ? 1 : 0) - (i.held.has('left') ? 1 : 0);
  // Without mouselook the hero's own facing is the basis (the camera may be orbited anywhere).
  if (steersFacing(i.scheme) && !i.buttons.right) basis = i.facing;
  if (f === 0 && r === 0) return { x: 0, y: 0 };
  const x = Math.cos(basis) * f + Math.sin(basis) * r;
  const y = Math.sin(basis) * f - Math.cos(basis) * r;
  const len = Math.hypot(x, y);
  return { x: round(x / len), y: round(y / len) };
}

/** Snap float noise so equal directions compare equal (same as bindings.moveDirection). */
const round = (v: number): number => Math.round(v * 1e6) / 1e6 || 0;
const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const angleDiff = (a: number, b: number): number => Math.abs(wrap(a - b));

/**
 * The local player's steering state: autorun and the client-steered facing (WoW controls).
 * `update` runs once per rendered frame; `moveDir` / `facingIntent` can be read any time
 * (key events ask for them between frames).
 */
export class Steering {
  scheme: ControlScheme = 'action';
  autorun = false;
  /** The hero's facing as the client sees it (steered, else the sim's). */
  facing = 0;
  /** The facing was steered by the client and the sim has not confirmed it yet. */
  private owned = false;
  private heldFor = 0;
  private bothWere = false;

  /** NumLock / Mouse4. */
  toggleAutorun(): void {
    this.autorun = !this.autorun;
  }

  /** A move key went down: W or S cancel autorun (A/D strafe along with it, turn keys turn). */
  keyDown(key: MoveKey): void {
    if (key === 'forward' || key === 'back') this.autorun = false;
  }

  /** Mouse buttons changed: pressing both cancels autorun (on the press, not while held). */
  buttonsChanged(b: MouseButtons): void {
    const both = b.left && b.right;
    if (both && !this.bothWere) this.autorun = false;
    this.bothWere = both;
  }

  input(held: ReadonlySet<MoveKey>, buttons: MouseButtons, cameraYaw: number): ControlInput {
    return { scheme: this.scheme, held, buttons, autorun: this.autorun, cameraYaw, facing: this.facing };
  }

  /** Move direction for the keys / buttons held now. */
  moveDir(held: ReadonlySet<MoveKey>, buttons: MouseButtons, cameraYaw: number): Vec2 {
    return controlMove(this.input(held, buttons, cameraYaw));
  }

  /** True while the camera steers the hero (WoW mouselook). Camera hook: don't auto-return then. */
  isSteering(buttons: MouseButtons): boolean {
    return isMouselook(this.scheme, buttons);
  }

  /**
   * The facing to send with this tick's commands: a number (action / WoW controls, client-steered
   * and not yet confirmed by the sim, or mouselook), or null (camera scheme: face the move
   * direction; action / WoW otherwise: leave the sim's facing alone, so strafing keeps it and a
   * cast's face-the-target still works).
   */
  facingIntent(buttons: MouseButtons): number | null {
    if (!steersFacing(this.scheme)) return null;
    return this.owned || buttons.right ? this.facing : null;
  }

  /**
   * Advance one frame. `simFacing`: the own unit's facing as a command sent now will find it
   * (the predicted view in a party run). `canTurn` false (dead, stunned) drops the steering.
   */
  update(dtMs: number, held: ReadonlySet<MoveKey>, buttons: MouseButtons, cameraYaw: number, simFacing: number, canTurn = true): void {
    if (!steersFacing(this.scheme) || !canTurn) {
      this.owned = false;
      this.facing = simFacing;
      return;
    }
    if (buttons.right) {
      this.facing = wrap(cameraYaw);
      this.owned = true;
      this.heldFor = 0;
      return;
    }
    const turn = turnAxis(this.scheme, held, buttons);
    if (turn !== 0) {
      const dir = this.moveDir(held, buttons, cameraYaw);
      const moving = dir.x !== 0 || dir.y !== 0;
      if (!this.owned) this.facing = simFacing;
      this.facing = wrap(this.facing + turn * TURN_RATE * (moving ? TURN_MOVING_FACTOR : 1) * Math.max(0, dtMs) / 1000);
      this.owned = true;
      this.heldFor = 0;
      return;
    }
    if (this.owned) {
      this.heldFor += Math.max(0, dtMs);
      if (angleDiff(simFacing, this.facing) < FACING_EPS || this.heldFor > FACING_HOLD_MS) this.owned = false;
    }
    if (!this.owned) this.facing = simFacing;
  }
}

/**
 * Live control state of the local player, written by the session every frame (read-only for
 * everyone else). Camera hook: e.g. don't auto-return the camera behind the hero while
 * `steering` (right-mouse mouselook), and follow `facing` while `turning` with the keys.
 */
export const localControl = {
  scheme: 'action' as ControlScheme,
  /** Mouselook: right mouse held, the camera yaw is the hero's facing. */
  steering: false,
  /** Turn keys (WoW controls: A/D) are turning the hero (no right mouse). */
  turning: false,
  /** Any mouse button is held on the game surface (a camera drag may be in progress). */
  dragging: false,
  autorun: false,
  /** Move keys / autorun are moving the hero. */
  moving: false,
  /** The forward axis (W, autorun, both buttons) is driving the hero (the camera follow return eases behind only then). */
  forward: false,
  /** The hero's present facing as the client draws it (null: no local hero). */
  facing: null as number | null,
};
